import React, { useState } from 'react';
import Head from 'next/head';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import useUser from 'hooks/use-user';

import { MobileOnly } from 'shared/components';
import { useModals, ModalNames } from 'components/modals';
import MetaTags from 'components/meta-tags';
import MyAccountDrawer from 'components/global-header/my-account-link/my-account-drawer';

import { AddressDialog } from './components/address-dialog';
import Footer from './components/footer';
import FooterAddition from './components/footer-addition';
import {
  palette,
  RedesignNav,
  RedesignHero,
  ShopLocalSection,
  OrderingStepsSection,
  PoweredExperienceSection,
  CategoriesSection,
  ReviewsStatsSection,
  CannabisYourWaySection,
  DarkFooterTheme,
  MenuBottomDrawerDark,
} from './redesign';

function HomePageRedesign({ showShopLocal = false }: { showShopLocal?: boolean }) {
  const { openModal } = useModals();
  const { isLoggedIn } = useUser();
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [showBottomDrawer, setShowBottomDrawer] = useState(false);
  const [showAccountDrawer, setShowAccountDrawer] = useState(false);

  const handleLoginClick = () => openModal(ModalNames.login);
  const handleSignUpClick = () => openModal(ModalNames.signUp);

  const handleCtaClick = () => {
    const isMobile = typeof window !== `undefined` && window.matchMedia(`(max-width: 600px)`).matches;

    if (isMobile) {
      setShowAddressDialog(true);
    } else {
      openModal(ModalNames.addressRequired);
    }
  };

  const toggleFooterModal = () => {
    setShowBottomDrawer((prev) => !prev);
  };

  const toggleAccountDrawer = () => {
    setShowAccountDrawer((prev) => !prev);
    setShowBottomDrawer(false);
  };

  return (
    <Wrapper>
      <MetaTags />
      <Head>
        <link
          rel='preload'
          href='/fonts/playfair-display-700italic.woff2'
          as='font'
          type='font/woff2'
          crossOrigin='anonymous'
        />
        {/* Critical: @font-face + CSS vars + body bg — all available before any JS */}
        <style>{`
          @font-face {
            font-family: 'Playfair Display';
            font-style: italic;
            font-weight: 700;
            font-display: block;
            src: url('/fonts/playfair-display-700italic.woff2') format('woff2');
          }
          :root{
            --rds-bg:#062f22;--rds-bg-deep:#042017;--rds-bg-card:#0e3528;
            --rds-plum:#4d2a3d;--rds-plum-dark:#3a1f2f;
            --rds-cream:#f3ead8;--rds-cream-dim:#cfc6b3;--rds-text-on-gold:#1a1a1a;
            --rds-gold:#e9b949;--rds-gold-bright:#f3c34a;--rds-gold-first:#ffc02a;
            --rds-input-bg:#0a1f1a;--rds-input-border:#1e4a3d;--rds-input-placeholder:#7a8a83;
            --rds-border:rgba(255,255,255,0.07);--rds-border-subtle:rgba(255,255,255,0.10);--rds-divider:rgba(255,255,255,0.15);
            --rds-serif:'Playfair Display',Georgia,serif;
          }
          html,body{background:#062f22;margin:0;padding:0}
        `}</style>
      </Head>
      <RedesignNav onToggleMobileMenu={toggleFooterModal} />
      {/* on our homepage, we want to apply #main-content to the dispensary search input */}
      <main>
        <RedesignHero onOpenAddressDialog={() => setShowAddressDialog(true)} />
        <MobileOnly mediaQuery='xs'>
          <AddressDialog onClose={() => setShowAddressDialog(false)} open={showAddressDialog} />
        </MobileOnly>

        {showShopLocal && <ShopLocalSection onCtaClick={handleCtaClick} />}
        <OrderingStepsSection />
        <PoweredExperienceSection />
        <CategoriesSection />
        <ReviewsStatsSection />
        <CannabisYourWaySection onCtaClick={handleCtaClick} />

        <MobileOnly>
          <MenuBottomDrawerDark
            open={showBottomDrawer}
            onClose={toggleFooterModal}
            toggleAccountDrawer={toggleAccountDrawer}
            isLoggedIn={isLoggedIn}
            onLoginClick={handleLoginClick}
            onSignUpClick={handleSignUpClick}
            onCtaClick={() => {
              setShowAddressDialog(true);
              setShowBottomDrawer(false);
            }}
          />
          <MyAccountDrawer open={showAccountDrawer && isLoggedIn} onClose={toggleAccountDrawer} />
        </MobileOnly>
      </main>
      <DarkFooterTheme>
        <FooterAddition />
        <Footer />
      </DarkFooterTheme>
    </Wrapper>
  );
}

export default observer(HomePageRedesign);

const Wrapper = styled.div`
  background: ${palette.bg};
  color: ${palette.cream};
  -webkit-font-smoothing: antialiased;
`;
